import { Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { AppConfigService } from './app-config.service';

export const IOS_STORE_URL = 'https://apps.apple.com/us/app/vesta-realty/id6737479817';
export const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.vesta.v2.app';

/**
 * True when `current` is a lower semver (major.minor.patch) than `minimum`.
 * Unparseable values fail open (do not block).
 */
export function isNativeVersionBelow(current: string, minimum: string): boolean {
    const a = parseVersion(current);
    const b = parseVersion(minimum);
    if (!a || !b) {
        return false;
    }
    for (let i = 0; i < 3; i++) {
        if (a[i] < b[i]) {
            return true;
        }
        if (a[i] > b[i]) {
            return false;
        }
    }
    return false;
}

function parseVersion(value: string): [number, number, number] | null {
    const core = String(value).trim().split(/[+-]/)[0];
    const match = core.match(/^(\d+)(?:\.(\d+))?(?:\.(\d+))?$/);
    if (!match) {
        return null;
    }
    return [Number(match[1]), Number(match[2] || 0), Number(match[3] || 0)];
}

/**
 * Native-only force upgrade. Reads Parse Config `minNativeVersion` (and optional
 * `iosStoreUrl` / `androidStoreUrl`). Unset or unreadable config does not block.
 * Web is skipped — CloudFront already ships the current JS.
 */
@Injectable()
export class ForceUpgradeService {
    blocking = false;

    constructor(private appConfig: AppConfigService) {}

    async enforceIfNeeded(): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        try {
            if (!this.appConfig.parseConfig) {
                await this.appConfig.fetchParseConfig();
            }

            const minimum = this.appConfig.parseConfig?.get('minNativeVersion');
            if (typeof minimum !== 'string' || !minimum.trim()) {
                return;
            }

            const info = await App.getInfo();
            if (!isNativeVersionBelow(info.version, minimum.trim())) {
                return;
            }

            this.blocking = true;
        } catch (error) {
            console.error('Force upgrade check failed; allowing app to continue.', error);
        }
    }

    openStore(): void {
        const url = this.storeUrl();
        window.open(url, '_blank');
    }

    storeUrl(): string {
        const platform = Capacitor.getPlatform();
        const fromConfig = platform === 'ios'
            ? this.appConfig.parseConfig?.get('iosStoreUrl')
            : this.appConfig.parseConfig?.get('androidStoreUrl');

        if (typeof fromConfig === 'string' && fromConfig.trim()) {
            return fromConfig.trim();
        }

        return platform === 'ios' ? IOS_STORE_URL : ANDROID_STORE_URL;
    }
}
